import type { ReactNode } from 'react';
import { RefreshCw, SearchCheck, TrendingUp, TriangleAlert } from 'lucide-react';
import { Banner, BrandIcon, Button, Card, EmptyState, Fact, Facts, LanguageName, MarketName, MetricTile, StatusPill } from '@/components/ui';
import { useSiteSearch } from '@/hooks/useSearchConsole';
import { useSeoTimeline, useSiteSeoMetrics } from '@/hooks/useSeoMetrics';
import { formatRelative } from '@/utils/format';
import { SEO_LEVEL, SEO_SEVERITY, seoIssueText } from '@/utils/seo';
import { deltaOf, number, pointsOf, trendOf } from '@/utils/seo-trends';
import type { SiteDetail, SiteSeoView } from '@/types/api';

export type SeoSection = 'summary' | 'search' | 'metrics' | 'technical';

const GOOGLE = (
  <>
    <BrandIcon name="googlesearchconsole" size={12} />
    28 дней
  </>
);
const AHREFS = (
  <>
    <TrendingUp size={12} aria-hidden="true" />
    Ahrefs
  </>
);

/**
 * The SEO tab's first page (ADR-0057): whether search engines may index the
 * site and what is wrong with it, and the figures that matter at a glance —
 * each tile leading to the tab with its detail.
 */
export function SeoSummary({
  site,
  view,
  mayEdit,
  checking,
  onCheck,
  onOpen,
}: {
  readonly site: SiteDetail;
  readonly view: SiteSeoView;
  readonly mayEdit: boolean;
  readonly checking: boolean;
  readonly onCheck: () => void;
  readonly onOpen: (section: SeoSection) => void;
}): ReactNode {
  const search = useSiteSearch(site.id);
  const metrics = useSiteSeoMetrics(site.id);
  const timeline = useSeoTimeline(site.id);
  const facts = view.facts;
  const home = facts?.home;
  const indexing = !home || home.status !== 200 ? null : !home.noindex && !facts!.robots.blocksAll;

  const data = search.data?.data ?? null;
  const connected = search.data?.state === 'verified';
  const totals = data?.totals ?? null;
  const previous = data?.previous ?? null;
  const inspected = data?.inspected ?? [];
  const indexed = inspected.filter((page) => page.verdict === 'PASS').length;
  const answered = inspected.filter((page) => page.verdict !== null).length;
  const gscHint = search.data?.available === false ? 'подключите Google' : connected ? undefined : 'подключите Search Console';

  const dr = trendOf(pointsOf(timeline.data, 'domain_rating', ['ahrefs']));
  const refs = trendOf(pointsOf(timeline.data, 'referring_domains'));
  const ahrefs = metrics.data?.ahrefs?.data ?? null;
  const refValue = ahrefs?.referringDomains ?? metrics.data?.dataforseo?.data?.referringDomains ?? metrics.data?.openpagerank?.data?.referringDomains ?? refs.current;

  return (
    <div className="stack">
      <Card
        title="Сводка"
        icon={SearchCheck}
        actions={
          mayEdit ? (
            <Button size="sm" icon={RefreshCw} busy={checking} disabled={view.requested} onClick={onCheck}>
              {view.requested ? 'Проверка в очереди' : 'Проверить сейчас'}
            </Button>
          ) : null
        }
      >
        <Facts>
          <Fact label="Состояние">
            <StatusPill map={SEO_LEVEL} status={view.level ?? 'none'} />
            {view.checkedAt ? <span className="muted"> · проверено {formatRelative(view.checkedAt)}</span> : null}
          </Fact>
          <Fact label="Индексация">
            {indexing === null ? (
              <span className="faint">неизвестно</span>
            ) : indexing ? (
              <span className={site.tier === 'staging' ? 'tone-degraded' : 'tone-healthy'}>открыта{site.tier === 'staging' ? ' (тестовый сайт)' : ''}</span>
            ) : (
              <span className={site.tier === 'staging' ? '' : 'tone-down'}>
                закрыта: {[home!.noindex ? 'noindex' : null, facts!.robots.blocksAll ? 'robots.txt' : null].filter(Boolean).join(', ')}
              </span>
            )}
          </Fact>
          <Fact label="Страна и язык">
            <span className="row-tight row-wrap">
              {site.country || site.language ? <MarketName country={site.country} language={site.language} /> : <span className="faint">не указаны</span>}
              {home?.lang ? (
                <span className="muted row-tight">
                  · страница: <LanguageName language={home.lang} />
                </span>
              ) : null}
            </span>
          </Fact>
        </Facts>
        {view.error ? (
          <Banner tone="warning" icon={TriangleAlert}>
            Последняя проверка не удалась: {view.error}
          </Banner>
        ) : null}
      </Card>

      <div className="metric-tiles">
        <MetricTile
          label="Клики"
          source={GOOGLE}
          value={connected ? number(totals?.clicks ?? 0) : null}
          delta={deltaOf(totals?.clicks ?? null, previous?.clicks ?? null, { percent: true })}
          history={(data?.daily ?? []).map((day) => day.clicks)}
          footnote={gscHint ?? (previous ? 'к прошлым 28 дням' : undefined)}
          onOpen={() => onOpen('search')}
        />
        <MetricTile
          label="Показы"
          source={GOOGLE}
          value={connected ? number(totals?.impressions ?? 0) : null}
          delta={deltaOf(totals?.impressions ?? null, previous?.impressions ?? null, { percent: true })}
          history={(data?.daily ?? []).map((day) => day.impressions)}
          footnote={gscHint}
          onOpen={() => onOpen('search')}
        />
        <MetricTile
          label="Средняя позиция"
          source={GOOGLE}
          value={totals ? number(totals.position, 1) : null}
          delta={deltaOf(totals?.position ?? null, previous?.position ?? null, { digits: 1 })}
          better="down"
          history={(data?.daily ?? []).flatMap((day) => (day.position !== undefined && day.impressions > 0 ? [day.position] : []))}
          footnote={gscHint}
          onOpen={() => onOpen('search')}
        />
        <MetricTile
          label="В индексе Google"
          source={<BrandIcon name="googlesearchconsole" size={12} />}
          value={answered > 0 ? `${indexed} из ${answered}` : null}
          footnote={answered > 0 ? 'ключевых страниц' : gscHint}
          onOpen={() => onOpen('search')}
        />
        <MetricTile
          label="Domain Rating"
          source={AHREFS}
          value={number(ahrefs?.domainRating ?? dr.current, 1)}
          delta={deltaOf(ahrefs?.domainRating ?? dr.current, dr.before, { digits: 1 })}
          history={dr.history}
          footnote={dr.before !== null ? 'за 30 дней' : ahrefs ? undefined : 'подключите Ahrefs'}
          onOpen={() => onOpen('metrics')}
        />
        <MetricTile
          label="Ссылающиеся домены"
          value={number(refValue)}
          delta={deltaOf(refs.current, refs.before)}
          history={refs.history}
          footnote={refs.before !== null ? 'за 30 дней' : undefined}
          onOpen={() => onOpen('metrics')}
        />
      </div>

      <Card flush title="Замечания" icon={TriangleAlert}>
        {!view.checkedAt ? (
          <EmptyState icon={SearchCheck} message="Проверок ещё не было" />
        ) : view.issues.length === 0 ? (
          <EmptyState icon={SearchCheck} message="Замечаний нет" />
        ) : (
          <ul className="list-plain seo-issues">
            {view.issues.map((issue, index) => (
              <li key={`${issue.code}-${index}`} className="row-tight">
                <StatusPill map={SEO_SEVERITY} status={issue.severity} />
                <span>{seoIssueText(issue)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
